import { IAlertsByAlarmReason } from "app/models/statistics.model";
import { IAlarmReasonTranslations } from "app/models/alarm-reasons.model";
import { Chart, ChartConfiguration, ChartDataset } from "chart.js";
import { Subscription, fromEvent } from "rxjs";
import { filter } from "rxjs/operators";
import { ALERTS_BY_ALARM_REASON_CHART_CONFIGS } from "app/shared/chart-configs.shared";
import { TranslateModule } from "@ngx-translate/core";
import { NgIf } from "@angular/common";

import {
  Component,
  OnInit,
  Input,
  HostListener,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { StatisticsExportCaptureService } from "app/services/statistics-export-capture.service";
import {
  StatisticsExportSnapshot,
  StatisticsExportWidget,
} from "app/services/statistics-export.models";
import { StatisticsPngDownloadBtnComponent } from "../statistics-png-download-btn/statistics-png-download-btn.component";

@Component({
  selector: "alerts-by-alarm-reason",
  templateUrl: "./alerts-by-alarm-reason.component.html",
  styleUrls: ["./alerts-by-alarm-reason.component.css"],
  standalone: true,
  imports: [TranslateModule, NgIf, StatisticsPngDownloadBtnComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertsByAlarmReasonComponent implements OnInit, OnDestroy, OnChanges, StatisticsExportWidget {
  /** Per-category row height at export (CSS px, before typography scale). */
  private static readonly EXPORT_ROW_HEIGHT_BASE_PX = 42;
  private static readonly EXPORT_CHART_PADDING_BASE_PX = 56;
  private static readonly CHART_EXPORT_WIDTH_PX = 1524;

  constructor(private captureService: StatisticsExportCaptureService) {}

  @Input() alertsByAlarmReasons: IAlertsByAlarmReason[];
  @Input() translations: IAlarmReasonTranslations;

  chart: Chart;

  currentColorIndex: number = 0;
  maxColorIndex: number = COLORS.length - 1;

  mouseRightClickSubscription: Subscription;

  pickColor(): string {
    const pickedColor = COLORS[this.currentColorIndex];
    this.currentColorIndex === this.maxColorIndex
      ? (this.currentColorIndex = 0)
      : this.currentColorIndex++;
    return pickedColor;
  }

  @HostListener("contextmenu", ["$event"])
  onContextMenu(event: MouseEvent) {
    event.preventDefault();
  }

  resetZoom() {
    // The document-wide right-click handler can fire before a chart exists (e.g.
    // on a tab where this widget has no data, so refreshChart() never runs), so
    // guard the chart itself — not just the optional resetZoom method.
    this.chart?.resetZoom?.();
  }

  getCategoryName(dictEntryKey: string): string {
    return this.translations?.alarmCategory?.find(
      (category) => category.key === dictEntryKey
    )?.translation;
  }

  getAlarmReasonName(dictEntryKey: string): string {
    // Fall back to the raw key when a translation is missing so dataset labels
    // (tooltips/legend/export) never become undefined.
    return (
      this.translations?.alarmReason?.find(
        (reason) => reason.key === dictEntryKey
      )?.translation ?? dictEntryKey
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes["alertsByAlarmReasons"] || changes["translations"]) &&
      this.alertsByAlarmReasons?.length &&
      this.translations
    ) {
      this.refreshChart();
    }
  }

  refreshChart(): void {
    if (!this.alertsByAlarmReasons?.length || !this.translations) {
      return;
    }
    this.releaseChartCanvas();
    this.currentColorIndex = 0;
    this.drawAlertsByAlarmReasonsChart();
  }

  private releaseChartCanvas(): void {
    this.chart?.destroy();
    this.chart = undefined;
    const canvas = document.getElementById(
      "alertsByAlarmReasonsChart"
    ) as HTMLCanvasElement | null;
    Chart.getChart(canvas ?? "alertsByAlarmReasonsChart")?.destroy();
  }

  private buildChartConfiguration(exportBarThicknessPx?: number): ChartConfiguration {
    // Save and reset the color index so the color sequence is always deterministic,
    // whether building for the live chart or for an off-screen export snapshot.
    const savedColorIndex = this.currentColorIndex;
    this.currentColorIndex = 0;

    const datasets: ChartDataset<"bar">[] = [];
    const categoryLabels: string[] = [];
    const maxCategoryAlertCount = Math.max(
      ...this.alertsByAlarmReasons.map((x) => x.totalAlerts)
    );

    const totalAlerts = this.alertsByAlarmReasons.reduce(
      (sum, alarmCategory) => sum + alarmCategory.totalAlerts,
      0
    );

    let i = 0;
    for (const category of this.alertsByAlarmReasons) {
      categoryLabels.push(this.getCategoryName(category.alarmReasonCategory));

      for (const alarmReason of category.alarmReasonAlerts) {
        const dataset: ChartDataset<"bar"> = {
          label: this.getAlarmReasonName(alarmReason.alarmReason),
          backgroundColor: this.pickColor(),
          maxBarThickness: exportBarThicknessPx ?? 18,
          data: [],
        };

        dataset.data[i] = alarmReason.alertCount;
        datasets.push(dataset);
      }
      i++;
    }

    this.currentColorIndex = savedColorIndex;
    const isExportLayout = exportBarThicknessPx != null;

    // Build a local config so the shared singleton is never mutated.
    return {
      ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS,
      data: {
        labels: categoryLabels,
        datasets: datasets.map((ds) => ({
          ...ds,
          data: Array.isArray(ds.data) ? [...ds.data] : ds.data,
        })),
      },
      options: {
        ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS.options,
        datasets: {
          bar: {
            categoryPercentage: isExportLayout ? 0.82 : 0.72,
            barPercentage: isExportLayout ? 0.94 : 0.86,
          },
        },
        scales: {
          ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS.options?.scales,
          x: {
            ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS.options?.scales?.['x'],
            max: maxCategoryAlertCount + 5,
          },
          y: {
            ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS.options?.scales?.['y'],
            ticks: {
              color: "#707070",
              font: { size: 12, weight: "600" },
              crossAlign: "far" as const,
              callback: (_: unknown, index: number) => {
                const size = 35;
                const label = categoryLabels[index];
                return (label?.length ?? 0) > size
                  ? label.substring(0, size - 3) + "..."
                  : label;
              },
            },
          },
        },
        plugins: {
          ...ALERTS_BY_ALARM_REASON_CHART_CONFIGS.options?.plugins,
          datalabels: {
            color: "#707070",
            anchor: "end" as const,
            align: "end" as const,
            formatter: (_: unknown, context: { chart: { data: { datasets: { data: unknown[] }[] } }; dataIndex: number; datasetIndex: number }) => {
              const chartDatasets = context.chart.data.datasets;
              const categoryAlerts: unknown[] = [];
              let lastCategoryDatasetIndex = 0;
              for (let d = 0; d < chartDatasets.length; d++) {
                if (chartDatasets[d].data[context.dataIndex] !== undefined) {
                  categoryAlerts.push(chartDatasets[d].data[context.dataIndex]);
                  lastCategoryDatasetIndex = d;
                }
              }
              const totalCategoryAlerts = (categoryAlerts as number[]).reduce(
                (total, datapoint) => total + datapoint, 0
              );
              if (context.datasetIndex === lastCategoryDatasetIndex) {
                // Guard against a zero/empty dataset so the label never renders
                // "NaN%" or "Infinity%".
                if (!totalAlerts) {
                  return "";
                }
                return ((totalCategoryAlerts / totalAlerts) * 100).toFixed(0) + "%";
              }
              return "";
            },
          },
        },
      },
      plugins: ALERTS_BY_ALARM_REASON_CHART_CONFIGS.plugins,
    };
  }

  drawAlertsByAlarmReasonsChart(): void {
    const config = this.buildChartConfiguration();
    this.chart = new Chart("alertsByAlarmReasonsChart", config);
    this.canvasHeight = this.alertsByAlarmReasons.length;
  }

  set canvasHeight(categoryCount: number) {
    const baseHeight = 200;
    const heightIncrement = 25 * categoryCount;

    const pixels = baseHeight + heightIncrement + "px";
    this.chart.canvas.parentElement.style.height = pixels;
  }

  private getChartExportHeightPx(): number {
    const categoryCount = Math.max(1, this.alertsByAlarmReasons?.length ?? 1);
    // Font size is fixed at 11px regardless of export width, so row height must not
    // scale with the export-width typography scale (which would yield ~100px rows for
    // 11px text).  Use the base values directly.
    const rowHeightPx = AlertsByAlarmReasonComponent.EXPORT_ROW_HEIGHT_BASE_PX;
    const paddingPx = AlertsByAlarmReasonComponent.EXPORT_CHART_PADDING_BASE_PX;
    // Extra headroom for chart layout padding, axis labels, and datalabels.
    return Math.ceil((paddingPx + categoryCount * rowHeightPx) * 1.12);
  }

  ngOnInit(): void {
    // Init listening mouse right click event
    const mouseRightClick$ = fromEvent<MouseEvent>(document, "mousedown").pipe(
      filter((event: MouseEvent) => event.button === 2)
    );

    this.mouseRightClickSubscription = mouseRightClick$.subscribe(() => {
      this.resetZoom();
    });
  }

  async captureForExport(): Promise<StatisticsExportSnapshot | null> {
    if (!this.alertsByAlarmReasons?.length) {
      return null;
    }

    // Font is fixed at 11px, so use the base row height (not the width-scaled value).
    const rowHeightPx = AlertsByAlarmReasonComponent.EXPORT_ROW_HEIGHT_BASE_PX;
    const exportBarThicknessPx = Math.min(22, Math.max(14, Math.floor(rowHeightPx * 0.45)));

    // Build the export config off-screen so the live chart is never mutated or reflowed.
    const exportConfig = this.buildChartConfiguration(exportBarThicknessPx);
    return this.captureService.buildOffscreenChartSnapshot(
      exportConfig,
      "alerts-by-alarm-reason",
      "ALERT_REASONS",
      {
        chartExportWidthPx: AlertsByAlarmReasonComponent.CHART_EXPORT_WIDTH_PX,
        chartExportHeightPx: this.getChartExportHeightPx(),
        chartDevicePixelRatio: 2,
      }
    );
  }

  ngOnDestroy(): void {
    this.mouseRightClickSubscription?.unsubscribe();
    this.releaseChartCanvas();
  }
}

const COLORS = [
  "rgb(112, 212, 252)",
  "rgb(0, 136, 227)",
  "rgb(75, 194, 186)",
  "rgb(255, 179, 153)",
  "rgb(255, 219, 160)",
  "rgb(143, 122, 219)",
  "rgb(152, 238, 206)",
  "rgb(255, 249, 179)",
  "rgb(196, 175, 148)",
  "rgb(234, 54, 127)",
  "rgb(204, 255, 252)",
  "rgb(255, 132, 83)",
  "rgb(198, 235, 203)",
  "rgb(255, 246, 133)",
  "rgb(255, 147, 54)",
  "rgb(230, 109, 126)",
  "rgb(129, 126, 227)",
  "rgb(94, 69, 75)",
  "rgb(255, 222, 248)",
  "rgb(27, 160, 148)",
];
